import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";

const REVIEW_ROLES = ["admin", "super-admin", "manager", "finance-head"];

const OVERALL_STATUS_OPTIONS = ["active", "closed"];
const AMC_YEAR_STATUS_OPTIONS = ["not-started", "active", "closed", "expired"];
const PAYMENT_STATUS_OPTIONS = ["pending", "partial", "paid"];

export default function AmcTasksPage() {
  const { user } = useContext(AuthContext);

  const role = String(user?.role || "").toLowerCase();
  const isReviewer = REVIEW_ROLES.includes(role);

  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showCreateForm, setShowCreateForm] = useState(false);

  const [filterStatus, setFilterStatus] = useState("active");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");

  const [form, setForm] = useState({
    workTitle: "",
    description: "",
    amcStartDate: "",
    totalAmcYears: "1",
    yearlyAmountINR: "",
  });

  const [errors, setErrors] = useState({});
  const setErr = (k, v) => setErrors((prev) => ({ ...prev, [k]: v ? String(v) : "" }));

  const [closeTaskModal, setCloseTaskModal] = useState({
    open: false,
    task: null,
    closeRemark: "",
  });

  const [editRemarkModal, setEditRemarkModal] = useState({
    open: false,
    task: null,
    latestRemark: "",
  });

  const [remarkHistoryModal, setRemarkHistoryModal] = useState({
    open: false,
    task: null,
  });

  const [yearEditModal, setYearEditModal] = useState({
    open: false,
    task: null,
    yearNo: 1,
    amcStatus: "active",
    paymentStatus: "pending",
    paidAmountINR: "",
    paymentDate: "",
    paymentRemark: "",
  });

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString("en-GB");
  };

  const fmtDateTime = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "—";
    return dt.toLocaleString("en-GB");
  };

  const fmtMoney = (n) =>
    Number.isFinite(Number(n))
      ? Number(n).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";

  const parseRemarkParts = (text = "") => {
    const parts = String(text)
      .split("|")
      .map((p) => p.trim())
      .filter(Boolean);

    const title =
      parts.find((p) => /^Year\s+\d+/i.test(p)) ||
      parts.find((p) => /^Remark/i.test(p)) ||
      "Update";

    const remarkPart = parts.find((p) => /^Remark\s+/i.test(p));
    const detailParts = parts.filter((p) => p !== title && p !== remarkPart);

    return {
      title,
      details: detailParts,
      remark: remarkPart ? remarkPart.replace(/^Remark\s+/i, "") : "",
    };
  };

  const buildQuery = useCallback(() => {
    const q = new URLSearchParams();
    if (filterStatus) q.append("status", filterStatus);
    if (filterPaymentStatus) q.append("paymentStatus", filterPaymentStatus);
    if (filterFromDate) q.append("from", filterFromDate);
    if (filterToDate) q.append("to", filterToDate);
    return q.toString();
  }, [filterStatus, filterPaymentStatus, filterFromDate, filterToDate]);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setErr("tasks", "");

    try {
      const qs = buildQuery();
      const url = qs ? `/api/amc?${qs}` : "/api/amc";
      const res = await axios.get(url, authHeader);
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setTasks([]);
      setErr(
        "tasks",
        `/api/amc → ${e?.response?.status || ""} ${
          e?.response?.data?.message || e.message
        }`
      );
    } finally {
      setLoading(false);
    }
  }, [authHeader, buildQuery]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleCreateTask = async (e) => {
    e.preventDefault();

    if (!form.workTitle.trim()) {
      alert("Please enter project/site name.");
      return;
    }

    if (!form.amcStartDate) {
      alert("Please select AMC start date.");
      return;
    }

    try {
      const payload = {
        workTitle: form.workTitle.trim(),
        description: form.description.trim(),
        amcStartDate: form.amcStartDate,
        totalAmcYears: Number(form.totalAmcYears || 1),
        yearlyAmountINR: Number(form.yearlyAmountINR || 0),
      };

      await axios.post("/api/amc", payload, authHeader);

      setForm({
        workTitle: "",
        description: "",
        amcStartDate: "",
        totalAmcYears: "1",
        yearlyAmountINR: "",
      });

      setShowCreateForm(false);
      await loadTasks();
      alert("AMC task created.");
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Failed to create AMC");
    }
  };

  const openYearEditModal = (task, yearPlan) => {
    setYearEditModal({
      open: true,
      task,
      yearNo: yearPlan.yearNo,
      amcStatus: yearPlan.amcStatus || "active",
      paymentStatus: yearPlan.paymentStatus || "pending",
      paidAmountINR: "",
      paymentDate: yearPlan.paymentDate
        ? new Date(yearPlan.paymentDate).toISOString().slice(0, 10)
        : "",
      paymentRemark: yearPlan.paymentRemark || "",
    });
  };

  const closeYearEditModal = () => {
    setYearEditModal({
      open: false,
      task: null,
      yearNo: 1,
      amcStatus: "active",
      paymentStatus: "pending",
      paidAmountINR: "",
      paymentDate: "",
      paymentRemark: "",
    });
  };

  const handleSubmitYearEdit = async (e) => {
    e.preventDefault();
    if (!yearEditModal.task?._id) return;

    try {
      await axios.patch(
        `/api/amc/${yearEditModal.task._id}/year/${yearEditModal.yearNo}`,
        {
          amcStatus: yearEditModal.amcStatus,
          paymentStatus: yearEditModal.paymentStatus,
          paidAmountINR: Number(yearEditModal.paidAmountINR || 0),
          paymentDate: yearEditModal.paymentDate || null,
          paymentRemark: yearEditModal.paymentRemark || "",
        },
        authHeader
      );

      closeYearEditModal();
      await loadTasks();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Failed to update year");
    }
  };

  const openCloseTaskModal = (task) => {
    setCloseTaskModal({
      open: true,
      task,
      closeRemark: task.latestRemark || "",
    });
  };

  const closeCloseTaskModal = () => {
    setCloseTaskModal({
      open: false,
      task: null,
      closeRemark: "",
    });
  };

  const handleSubmitCloseTask = async (e) => {
    e.preventDefault();
    if (!closeTaskModal.task?._id) return;

    try {
      await axios.patch(
        `/api/amc/${closeTaskModal.task._id}/close`,
        { closeRemark: closeTaskModal.closeRemark || "" },
        authHeader
      );

      closeCloseTaskModal();
      await loadTasks();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Failed to close AMC");
    }
  };

  const openEditRemarkModal = (task) => {
    setEditRemarkModal({
      open: true,
      task,
      latestRemark: task.latestRemark || "",
    });
  };

  const closeEditRemarkModal = () => {
    setEditRemarkModal({
      open: false,
      task: null,
      latestRemark: "",
    });
  };

  const handleSubmitEditRemark = async (e) => {
    e.preventDefault();
    if (!editRemarkModal.task?._id) return;

    try {
      await axios.patch(
        `/api/amc/${editRemarkModal.task._id}`,
        { latestRemark: editRemarkModal.latestRemark || "" },
        authHeader
      );

      closeEditRemarkModal();
      await loadTasks();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Failed to update remark");
    }
  };

  const maxYears = useMemo(() => {
    return Math.max(
      1,
      ...tasks.map((t) => Number(t.totalAmcYears || t.yearPlans?.length || 1))
    );
  }, [tasks]);

  const activeCount = tasks.filter((t) => t.status === "active").length;
  const closedCount = tasks.filter((t) => t.status === "closed").length;

  const totalPayment = tasks.reduce(
    (sum, t) => sum + Number(t.totalContractAmountINR || 0),
    0
  );

  const totalPaid = tasks.reduce((sum, t) => {
    const paidForTask = (t.yearPlans || []).reduce(
      (s, y) => s + Number(y.paidAmountINR || 0),
      0
    );
    return sum + paidForTask;
  }, 0);

  const totalRemaining = Math.max(totalPayment - totalPaid, 0);

  const currentYearPaymentToReceive = tasks.reduce((sum, t) => {
    const currentYearPlan = (t.yearPlans || []).find((y) => y.amcStatus === "active");
    if (!currentYearPlan) return sum;

    const amount = Number(currentYearPlan.amcAmountINR || 0);
    const paid = Number(currentYearPlan.paidAmountINR || 0);
    return sum + Math.max(amount - paid, 0);
  }, 0);

  const getStatusPillStyle = (status) => {
    const s = String(status || "").toLowerCase();

    if (s === "paid" || s === "active") {
      return {
        background: "rgba(16, 185, 129, 0.16)",
        color: "#065f46",
        border: "1px solid rgba(16, 185, 129, 0.25)",
      };
    }

    if (s === "partial" || s === "expired") {
      return {
        background: "rgba(245, 158, 11, 0.16)",
        color: "#92400e",
        border: "1px solid rgba(245, 158, 11, 0.25)",
      };
    }

    if (s === "pending" || s === "closed") {
      return {
        background: "rgba(239, 68, 68, 0.12)",
        color: "#991b1b",
        border: "1px solid rgba(239, 68, 68, 0.22)",
      };
    }

    return {
      background: "#eef2ff",
      color: "#3730a3",
      border: "1px solid #c7d2fe",
    };
  };

  const getYearCellStyle = (paymentStatus) => {
    const s = String(paymentStatus || "").toLowerCase();

    if (s === "paid") {
      return {
        background:
          "linear-gradient(180deg, rgba(220,252,231,1) 0%, rgba(240,253,244,1) 100%)",
        border: "1px solid #bbf7d0",
      };
    }

    if (s === "partial") {
      return {
        background:
          "linear-gradient(180deg, rgba(254,243,199,1) 0%, rgba(255,251,235,1) 100%)",
        border: "1px solid #fcd34d",
      };
    }

    if (s === "pending") {
      return {
        background:
          "linear-gradient(180deg, rgba(254,226,226,1) 0%, rgba(254,242,242,1) 100%)",
        border: "1px solid #fecaca",
      };
    }

    return {
      background:
        "linear-gradient(180deg, rgba(241,245,249,1) 0%, rgba(248,250,252,1) 100%)",
      border: "1px solid #e2e8f0",
    };
  };

  return (
    <div style={pageWrap}>
      <div style={heroCard}>
        <div style={heroTop}>
          <div>
            <h1 style={pageTitle}>AMC Management</h1>
            <p style={pageSubtitle}>
              Track AMC projects in a year-wise sheet view like Excel for much easier review.
            </p>
          </div>

          <button
            type="button"
            style={primaryBtn}
            onClick={() => setShowCreateForm((s) => !s)}
          >
            {showCreateForm ? "Hide Add AMC Task" : "Add AMC Task"}
          </button>
        </div>

        <div style={summaryGrid}>
          <StatCard label="Active" value={activeCount} />
          <StatCard label="Closed" value={closedCount} />
          <StatCard label="Total Payment" value={`₹ ${fmtMoney(totalPayment)}`} />
          <StatCard label="Total Paid" value={`₹ ${fmtMoney(totalPaid)}`} />
          <StatCard label="Total Remaining" value={`₹ ${fmtMoney(totalRemaining)}`} />
          <StatCard
            label="This Year Payment to Receive"
            value={`₹ ${fmtMoney(currentYearPaymentToReceive)}`}
          />
        </div>
      </div>

      {showCreateForm && (
        <div style={card}>
          <div style={sectionHeader}>
            <div>
              <div style={sectionTitle}>Add AMC Task</div>
              <div style={sectionSubtext}>
                Create a project with AMC start date, duration, and yearly amount.
              </div>
            </div>
          </div>

          <form onSubmit={handleCreateTask}>
            <div style={formGrid}>
              <Field label="Project / Site Name *">
                <input
                  style={inputStyle}
                  type="text"
                  value={form.workTitle}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, workTitle: e.target.value }))
                  }
                  placeholder="e.g. Park Street Water ATM"
                />
              </Field>

              <Field label="AMC Start Date *">
                <input
                  style={inputStyle}
                  type="date"
                  value={form.amcStartDate}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amcStartDate: e.target.value }))
                  }
                />
              </Field>

              <Field label="Total AMC Years *">
                <input
                  style={inputStyle}
                  type="number"
                  min="1"
                  step="1"
                  value={form.totalAmcYears}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, totalAmcYears: e.target.value }))
                  }
                />
              </Field>

              <Field label="Yearly AMC Amount (₹)">
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.yearlyAmountINR}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, yearlyAmountINR: e.target.value }))
                  }
                  placeholder="e.g. 12000"
                />
              </Field>

              <div style={{ gridColumn: "1 / -1" }}>
                <Field label="Description">
                  <textarea
                    style={{ ...inputStyle, minHeight: 110, resize: "vertical" }}
                    rows={4}
                    value={form.description}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, description: e.target.value }))
                    }
                    placeholder="Scope, customer, notes"
                  />
                </Field>
              </div>
            </div>

            <div style={actionsRow}>
              <button type="submit" style={primaryBtn}>
                Save AMC Task
              </button>
            </div>
          </form>
        </div>
      )}

      {!!Object.values(errors).filter(Boolean).length && (
        <div style={debugCard}>
          <div style={sectionTitle}>Debug</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: "#6b7280" }}>
            {Object.entries(errors).map(([k, v]) =>
              v ? (
                <li key={k}>
                  <code>{k}</code>: {v}
                </li>
              ) : null
            )}
          </ul>
        </div>
      )}

      <div style={card}>
        <div style={sectionHeader}>
          <div>
            <div style={sectionTitle}>AMC Sheet View</div>
            <div style={sectionSubtext}>
              One row per project and one year per column.
            </div>
          </div>

          <div style={contractSummaryWrap}>
            <div style={contractPill}>Total Payment: ₹ {fmtMoney(totalPayment)}</div>
            <div style={contractPill}>Total Paid: ₹ {fmtMoney(totalPaid)}</div>
            <div style={contractPill}>Remaining: ₹ {fmtMoney(totalRemaining)}</div>
            <div style={contractPill}>
              This Year Receive: ₹ {fmtMoney(currentYearPaymentToReceive)}
            </div>
          </div>
        </div>

        <div style={filterGrid}>
          <Field label="Status">
            <select
              style={inputStyle}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All</option>
              {OVERALL_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Payment Status">
            <select
              style={inputStyle}
              value={filterPaymentStatus}
              onChange={(e) => setFilterPaymentStatus(e.target.value)}
            >
              <option value="">All</option>
              {PAYMENT_STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>

          <Field label="From Date">
            <input
              style={inputStyle}
              type="date"
              value={filterFromDate}
              onChange={(e) => setFilterFromDate(e.target.value)}
            />
          </Field>

          <Field label="To Date">
            <input
              style={inputStyle}
              type="date"
              value={filterToDate}
              onChange={(e) => setFilterToDate(e.target.value)}
            />
          </Field>
        </div>

        <div style={toolbarRow}>
          <div style={mutedText}>
            Showing <strong>{tasks.length}</strong> task{tasks.length === 1 ? "" : "s"}
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              style={secondaryBtn}
              type="button"
              onClick={() => {
                setFilterStatus("active");
                setFilterPaymentStatus("");
                setFilterFromDate("");
                setFilterToDate("");
              }}
            >
              Clear
            </button>

            <button style={primaryBtn} type="button" onClick={loadTasks}>
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        <div style={tableShell}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th sticky left={0} minWidth={250}>
                  Project / Site
                </Th>
                <Th sticky left={250} minWidth={125}>
                  Start Date
                </Th>
                <Th minWidth={95}>Years</Th>
                <Th minWidth={140}>Total Value</Th>

                {Array.from({ length: maxYears }).map((_, index) => (
                  <Th key={index} minWidth={220}>
                    Year {index + 1}
                  </Th>
                ))}

                <Th minWidth={135}>Total Paid</Th>
                <Th minWidth={140}>Remaining</Th>
                <Th minWidth={120}>Status</Th>
                <Th minWidth={210}>Actions</Th>
              </tr>
            </thead>

            <tbody>
              {tasks.map((t) => {
                const taskPaid = (t.yearPlans || []).reduce(
                  (sum, y) => sum + Number(y.paidAmountINR || 0),
                  0
                );

                const taskTotal = Number(t.totalContractAmountINR || 0);
                const taskRemaining = Math.max(taskTotal - taskPaid, 0);

                return (
                  <tr key={t._id}>
                    <Td sticky left={0} minWidth={250}>
                      <div style={projectNameCell}>
                        <div style={projectName}>{t.workTitle || "Untitled Project"}</div>
                        <div style={projectDesc}>{t.description || "—"}</div>
                      </div>
                    </Td>

                    <Td sticky left={250} minWidth={125}>
                      {fmtDate(t.amcStartDate)}
                    </Td>

                    <Td>{t.totalAmcYears || 0}</Td>

                    <Td>₹ {fmtMoney(t.totalContractAmountINR || 0)}</Td>

                    {Array.from({ length: maxYears }).map((_, index) => {
                      const yearNo = index + 1;
                      const y = (t.yearPlans || []).find((item) => item.yearNo === yearNo);

                      if (!y) {
                        return (
                          <Td key={yearNo} minWidth={220}>
                            <div style={emptyYearCard}>—</div>
                          </Td>
                        );
                      }

                      const amount = Number(y.amcAmountINR || 0);
                      const paid = Number(y.paidAmountINR || 0);
                      const remaining = Math.max(amount - paid, 0);

                      return (
                        <Td key={yearNo} minWidth={220}>
                          <div style={{ ...yearCard, ...getYearCellStyle(y.paymentStatus) }}>
                            <div style={yearCardTop}>
                              <div style={yearCardTitle}>Year {y.yearNo}</div>
                              <button
                                type="button"
                                style={miniBtn}
                                onClick={() => openYearEditModal(t, y)}
                              >
                                Edit
                              </button>
                            </div>

                            <div style={yearMetaRow}>
                              <span style={yearMetaLabel}>Duration</span>
                              <span style={yearMetaValue}>
                                {fmtDate(y.startDate)} - {fmtDate(y.endDate)}
                              </span>
                            </div>

                            <div style={yearMetaRow}>
                              <span style={yearMetaLabel}>AMC</span>
                              <span style={yearMetaValue}>₹ {fmtMoney(amount)}</span>
                            </div>

                            <div style={yearMetaRow}>
                              <span style={yearMetaLabel}>Paid</span>
                              <span style={yearMetaValue}>₹ {fmtMoney(paid)}</span>
                            </div>

                            <div style={yearMetaRow}>
                              <span style={yearMetaLabel}>Balance</span>
                              <span style={yearMetaValue}>₹ {fmtMoney(remaining)}</span>
                            </div>

                            <div style={yearPills}>
                              <span style={{ ...pillBase, ...getStatusPillStyle(y.amcStatus) }}>
                                AMC: {y.amcStatus}
                              </span>
                              <span
                                style={{ ...pillBase, ...getStatusPillStyle(y.paymentStatus) }}
                              >
                                Pay: {y.paymentStatus}
                              </span>
                            </div>

                            <div style={yearRemark}>
                              {y.paymentRemark || <span style={smallMuted}>No remark</span>}
                            </div>
                          </div>
                        </Td>
                      );
                    })}

                    <Td>₹ {fmtMoney(taskPaid)}</Td>
                    <Td>₹ {fmtMoney(taskRemaining)}</Td>

                    <Td>
                      <span style={{ ...pillBase, ...getStatusPillStyle(t.status) }}>
                        {t.status}
                      </span>
                    </Td>

                    <Td>
                      <div style={actionStack}>
                        <button
                          type="button"
                          style={secondaryBtn}
                          onClick={() => openEditRemarkModal(t)}
                        >
                          Edit Remark
                        </button>

                        {!!t.remarksHistory?.length && (
                          <button
                            type="button"
                            style={secondaryBtn}
                            onClick={() => setRemarkHistoryModal({ open: true, task: t })}
                          >
                            History ({t.remarksHistory.length})
                          </button>
                        )}

                        <button
                          type="button"
                          style={dangerBtn}
                          onClick={() => openCloseTaskModal(t)}
                          disabled={!isReviewer || t.status === "closed"}
                        >
                          Close AMC
                        </button>
                      </div>
                    </Td>
                  </tr>
                );
              })}

              {!tasks.length && (
                <tr>
                  <td colSpan={4 + maxYears + 4} style={emptyStateCell}>
                    No AMC tasks found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {yearEditModal.open && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <div>
                <div style={sectionTitle}>Update AMC Year {yearEditModal.yearNo}</div>
                <div style={sectionSubtext}>
                  Update service completion and payment details.
                </div>
              </div>
              <button style={iconBtn} onClick={closeYearEditModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitYearEdit}>
              <div style={formGrid}>
                <Field label="AMC Status">
                  <select
                    style={inputStyle}
                    value={yearEditModal.amcStatus}
                    onChange={(e) =>
                      setYearEditModal((m) => ({
                        ...m,
                        amcStatus: e.target.value,
                      }))
                    }
                  >
                    {AMC_YEAR_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Payment Status">
                  <select
                    style={inputStyle}
                    value={yearEditModal.paymentStatus}
                    onChange={(e) =>
                      setYearEditModal((m) => ({
                        ...m,
                        paymentStatus: e.target.value,
                      }))
                    }
                  >
                    {PAYMENT_STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Add Payment Now (₹)">
                  <input
                    style={inputStyle}
                    type="number"
                    min="0"
                    step="0.01"
                    value={yearEditModal.paidAmountINR}
                    onChange={(e) =>
                      setYearEditModal((m) => ({
                        ...m,
                        paidAmountINR: e.target.value,
                      }))
                    }
                    placeholder="Enter new received amount"
                  />
                </Field>

                <Field label="Payment Date">
                  <input
                    style={inputStyle}
                    type="date"
                    value={yearEditModal.paymentDate}
                    onChange={(e) =>
                      setYearEditModal((m) => ({
                        ...m,
                        paymentDate: e.target.value,
                      }))
                    }
                  />
                </Field>

                <div style={{ gridColumn: "1 / -1" }}>
                  <Field label="Payment Remark">
                    <textarea
                      style={{ ...inputStyle, minHeight: 110, resize: "vertical" }}
                      rows={4}
                      value={yearEditModal.paymentRemark}
                      onChange={(e) =>
                        setYearEditModal((m) => ({
                          ...m,
                          paymentRemark: e.target.value,
                        }))
                      }
                    />
                  </Field>
                </div>
              </div>

              <div style={modalActions}>
                <button type="button" style={secondaryBtn} onClick={closeYearEditModal}>
                  Cancel
                </button>
                <button type="submit" style={primaryBtn}>
                  Save Year Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {closeTaskModal.open && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <div>
                <div style={sectionTitle}>Close AMC</div>
                <div style={sectionSubtext}>This will mark the AMC as closed.</div>
              </div>
              <button style={iconBtn} onClick={closeCloseTaskModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitCloseTask}>
              <Field label="Close Remark">
                <textarea
                  style={{ ...inputStyle, minHeight: 120, resize: "vertical" }}
                  rows={4}
                  value={closeTaskModal.closeRemark}
                  onChange={(e) =>
                    setCloseTaskModal((m) => ({
                      ...m,
                      closeRemark: e.target.value,
                    }))
                  }
                />
              </Field>

              <div style={modalActions}>
                <button type="button" style={secondaryBtn} onClick={closeCloseTaskModal}>
                  Cancel
                </button>
                <button type="submit" style={dangerBtn}>
                  Close AMC
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editRemarkModal.open && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <div>
                <div style={sectionTitle}>Edit Remark</div>
                <div style={sectionSubtext}>
                  Update latest AMC remark for quick review.
                </div>
              </div>
              <button style={iconBtn} onClick={closeEditRemarkModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitEditRemark}>
              <Field label="Latest Remark">
                <textarea
                  style={{ ...inputStyle, minHeight: 120, resize: "vertical" }}
                  rows={4}
                  value={editRemarkModal.latestRemark}
                  onChange={(e) =>
                    setEditRemarkModal((m) => ({
                      ...m,
                      latestRemark: e.target.value,
                    }))
                  }
                />
              </Field>

              <div style={modalActions}>
                <button type="button" style={secondaryBtn} onClick={closeEditRemarkModal}>
                  Cancel
                </button>
                <button type="submit" style={primaryBtn}>
                  Save Remark
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {remarkHistoryModal.open && (
        <div style={modalBackdrop}>
          <div style={historyModalCard}>
            <div style={modalHeader}>
              <div>
                <div style={sectionTitle}>Remark History</div>
                <div style={sectionSubtext}>All recorded AMC remark updates.</div>
              </div>
              <button
                style={iconBtn}
                onClick={() => setRemarkHistoryModal({ open: false, task: null })}
              >
                ✕
              </button>
            </div>

            {remarkHistoryModal.task?.remarksHistory?.length ? (
              <div style={historyList}>
                {[...(remarkHistoryModal.task.remarksHistory || [])]
                  .reverse()
                  .map((r, idx) => {
                    const parsed = parseRemarkParts(r.text);

                    return (
                      <div key={idx} style={historyCard}>
                        <div style={historyTopRow}>
                          <div style={historyTitle}>{parsed.title}</div>
                          <div style={historyDate}>{fmtDateTime(r.createdAt)}</div>
                        </div>

                        {!!parsed.details.length && (
                          <div style={historyChipsWrap}>
                            {parsed.details.map((item, i) => (
                              <span key={i} style={historyChip}>
                                {item}
                              </span>
                            ))}
                          </div>
                        )}

                        {!!parsed.remark && (
                          <div style={historyRemarkBox}>
                            <div style={historyRemarkLabel}>Remark</div>
                            <div style={historyRemarkValue}>{parsed.remark}</div>
                          </div>
                        )}

                        <div style={historyFooter}>
                          Updated by <strong>{r.createdByName || "Unknown"}</strong>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div style={emptyHistory}>No remark history found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <label
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "#0f172a",
          letterSpacing: 0.2,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={statCard}>
      <div style={statLabel}>{label}</div>
      <div
        style={{
          ...statValue,
          fontSize: typeof value === "string" && value.includes("₹") ? 21 : 30,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Th({ children, sticky = false, left = 0, minWidth }) {
  return (
    <th
      style={{
        ...thStyle,
        ...(sticky
          ? {
              position: "sticky",
              left,
              zIndex: 3,
              background: "#eef4ff",
            }
          : {}),
        ...(minWidth ? { minWidth } : {}),
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, sticky = false, left = 0, minWidth }) {
  return (
    <td
      style={{
        ...tdStyle,
        ...(sticky
          ? {
              position: "sticky",
              left,
              zIndex: 2,
              background: "#ffffff",
            }
          : {}),
        ...(minWidth ? { minWidth } : {}),
      }}
    >
      {children}
    </td>
  );
}

const pageWrap = {
  padding: 24,
  background:
    "linear-gradient(180deg, #f7faff 0%, #eef4ff 45%, #f8fbff 100%)",
  minHeight: "100vh",
  color: "#0f172a",
};

const heroCard = {
  background: "linear-gradient(135deg, #ffffff 0%, #f2f7ff 100%)",
  border: "1px solid #d9e7ff",
  borderRadius: 24,
  padding: 24,
  boxShadow: "0 16px 50px rgba(37, 99, 235, 0.08)",
  marginBottom: 18,
};

const heroTop = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  alignItems: "flex-start",
  flexWrap: "wrap",
  marginBottom: 18,
};

const pageTitle = {
  margin: 0,
  fontSize: 34,
  lineHeight: 1.1,
  fontWeight: 800,
  color: "#0b1f44",
};

const pageSubtitle = {
  margin: "10px 0 0",
  fontSize: 15,
  lineHeight: 1.6,
  color: "#50627f",
  maxWidth: 760,
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 14,
};

const statCard = {
  background: "#ffffff",
  border: "1px solid #dbeafe",
  borderRadius: 20,
  padding: "18px 16px",
  boxShadow: "0 8px 24px rgba(37, 99, 235, 0.06)",
};

const statLabel = {
  fontSize: 13,
  fontWeight: 700,
  color: "#5b6b85",
  marginBottom: 8,
};

const statValue = {
  fontWeight: 800,
  color: "#102a56",
  lineHeight: 1.15,
};

const card = {
  background: "#ffffff",
  border: "1px solid #dbeafe",
  borderRadius: 24,
  padding: 22,
  boxShadow: "0 12px 34px rgba(15, 23, 42, 0.05)",
  marginBottom: 18,
};

const debugCard = {
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  borderRadius: 18,
  padding: 18,
  marginBottom: 18,
};

const sectionHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
  alignItems: "flex-start",
  marginBottom: 18,
};

const sectionTitle = {
  fontSize: 22,
  fontWeight: 800,
  color: "#0f172a",
};

const sectionSubtext = {
  marginTop: 6,
  color: "#64748b",
  fontSize: 14,
  lineHeight: 1.55,
};

const formGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
};

const inputStyle = {
  width: "100%",
  height: 46,
  borderRadius: 14,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  padding: "0 14px",
  fontSize: 14,
  color: "#0f172a",
  outline: "none",
  boxSizing: "border-box",
};

const actionsRow = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: 18,
};

const primaryBtn = {
  border: "none",
  borderRadius: 14,
  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
  color: "#ffffff",
  fontWeight: 800,
  padding: "12px 18px",
  cursor: "pointer",
  boxShadow: "0 10px 22px rgba(37, 99, 235, 0.25)",
};

const secondaryBtn = {
  border: "1px solid #cbd5e1",
  borderRadius: 12,
  background: "#ffffff",
  color: "#0f172a",
  fontWeight: 700,
  padding: "10px 14px",
  cursor: "pointer",
};

const dangerBtn = {
  border: "none",
  borderRadius: 12,
  background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
  color: "#ffffff",
  fontWeight: 800,
  padding: "10px 14px",
  cursor: "pointer",
};

const miniBtn = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#ffffff",
  color: "#0f172a",
  fontWeight: 700,
  padding: "6px 10px",
  cursor: "pointer",
  fontSize: 12,
};

const contractSummaryWrap = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

const contractPill = {
  padding: "9px 12px",
  borderRadius: 999,
  background: "#eff6ff",
  color: "#1d4ed8",
  border: "1px solid #bfdbfe",
  fontSize: 13,
  fontWeight: 800,
};

const filterGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 14,
  marginBottom: 16,
};

const toolbarRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginBottom: 18,
};

const mutedText = {
  fontSize: 14,
  color: "#64748b",
};

const tableShell = {
  overflowX: "auto",
  borderRadius: 20,
  border: "1px solid #dbeafe",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  minWidth: 1300,
  background: "#ffffff",
};

const thStyle = {
  textAlign: "left",
  padding: "16px 14px",
  fontSize: 13,
  fontWeight: 800,
  color: "#16315f",
  background: "#eef4ff",
  borderBottom: "1px solid #dbeafe",
  whiteSpace: "nowrap",
  top: 0,
};

const tdStyle = {
  padding: "14px",
  borderBottom: "1px solid #edf2f7",
  verticalAlign: "top",
  background: "#ffffff",
  fontSize: 14,
  color: "#0f172a",
};

const projectNameCell = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const projectName = {
  fontSize: 15,
  fontWeight: 800,
  color: "#102a56",
};

const projectDesc = {
  fontSize: 13,
  lineHeight: 1.5,
  color: "#64748b",
};

const yearCard = {
  borderRadius: 16,
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  minHeight: 170,
};

const emptyYearCard = {
  minHeight: 170,
  borderRadius: 16,
  display: "grid",
  placeItems: "center",
  color: "#94a3b8",
  background: "#f8fafc",
  border: "1px dashed #dbe2ea",
};

const yearCardTop = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
};

const yearCardTitle = {
  fontSize: 14,
  fontWeight: 800,
  color: "#102a56",
};

const yearMetaRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "flex-start",
};

const yearMetaLabel = {
  fontSize: 12,
  color: "#64748b",
  fontWeight: 700,
};

const yearMetaValue = {
  fontSize: 12,
  color: "#0f172a",
  fontWeight: 800,
  textAlign: "right",
};

const yearPills = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  marginTop: 4,
};

const pillBase = {
  padding: "5px 9px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 800,
  display: "inline-flex",
  alignItems: "center",
};

const yearRemark = {
  marginTop: "auto",
  fontSize: 12,
  lineHeight: 1.5,
  color: "#334155",
  background: "rgba(255,255,255,0.55)",
  borderRadius: 10,
  padding: "8px 9px",
};

const smallMuted = {
  color: "#94a3b8",
};

const actionStack = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const emptyStateCell = {
  textAlign: "center",
  padding: "28px 14px",
  color: "#64748b",
  fontWeight: 700,
};

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 999,
};

const modalCard = {
  width: "100%",
  maxWidth: 760,
  background: "#ffffff",
  borderRadius: 24,
  padding: 22,
  boxShadow: "0 24px 60px rgba(15,23,42,0.25)",
  maxHeight: "90vh",
  overflowY: "auto",
};

const historyModalCard = {
  width: "100%",
  maxWidth: 860,
  background: "#ffffff",
  borderRadius: 24,
  padding: 22,
  boxShadow: "0 24px 60px rgba(15,23,42,0.25)",
  maxHeight: "90vh",
  overflowY: "auto",
};

const modalHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  alignItems: "flex-start",
  marginBottom: 18,
};

const iconBtn = {
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  borderRadius: 12,
  width: 38,
  height: 38,
  cursor: "pointer",
  fontSize: 16,
  fontWeight: 800,
};

const modalActions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  marginTop: 18,
  flexWrap: "wrap",
};

const historyList = {
  display: "grid",
  gap: 14,
};

const historyCard = {
  border: "1px solid #e2e8f0",
  borderRadius: 18,
  padding: 16,
  background: "#f8fbff",
};

const historyTopRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  alignItems: "center",
  flexWrap: "wrap",
  marginBottom: 10,
};

const historyTitle = {
  fontSize: 15,
  fontWeight: 800,
  color: "#102a56",
};

const historyDate = {
  fontSize: 12,
  color: "#64748b",
  fontWeight: 700,
};

const historyChipsWrap = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  marginBottom: 10,
};

const historyChip = {
  padding: "6px 10px",
  borderRadius: 999,
  background: "#e0f2fe",
  color: "#075985",
  fontSize: 12,
  fontWeight: 800,
};

const historyRemarkBox = {
  borderRadius: 14,
  padding: 12,
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  marginBottom: 10,
};

const historyRemarkLabel = {
  fontSize: 12,
  fontWeight: 800,
  color: "#64748b",
  marginBottom: 4,
};

const historyRemarkValue = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "#0f172a",
};

const historyFooter = {
  fontSize: 12,
  color: "#475569",
};

const emptyHistory = {
  textAlign: "center",
  padding: "28px 12px",
  color: "#64748b",
  fontWeight: 700,
};